/**
 * cacheStatusStore — drives the status-bar pill that appears while one of
 * the project caches is refreshing in the background. Each cache (file tree,
 * search index, TS workspace, symbols) registers a busy "scope" while it
 * works; the status bar shows a summary as long as any scope is active.
 *
 * Use begin() / end() in a try/finally pair around the work.
 */
import { create } from 'zustand';

export type CacheScope =
  | 'file-tree'
  | 'file-index'
  | 'ts-workspace'
  | 'symbols';

const SCOPE_LABEL: Record<CacheScope, string> = {
  'file-tree': 'file tree',
  'file-index': 'search index',
  'ts-workspace': 'TS workspace',
  symbols: 'symbol index',
};

interface CacheStatusState {
  active: Set<CacheScope>;
  begin: (scope: CacheScope) => void;
  end: (scope: CacheScope) => void;
  /** Human-readable summary like "Refreshing file tree, search index..." or "" if idle. */
  label: () => string;
}

export const useCacheStatusStore = create<CacheStatusState>((set, get) => ({
  active: new Set(),
  begin: (scope) =>
    set((s) => {
      if (s.active.has(scope)) return s;
      const next = new Set(s.active);
      next.add(scope);
      return { active: next };
    }),
  end: (scope) =>
    set((s) => {
      if (!s.active.has(scope)) return s;
      const next = new Set(s.active);
      next.delete(scope);
      return { active: next };
    }),
  label: () => {
    const items = Array.from(get().active).map((s) => SCOPE_LABEL[s]);
    if (items.length === 0) return '';
    return `Refreshing ${items.join(', ')}…`;
  },
}));
